import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { For, Show } from "solid-js";
import { JOURNEY_FRICTION_KIND } from "../../enums/scan";
import { JourneyStructuralEvidence } from "./JourneyStructuralEvidence";
import type {
  CandidateJourney,
  PriorityContentFlow,
  PriorityFlowConcern,
} from "./journeyTypes";
import { pageDetailHref } from "./pageDetailNavigation";

export function JourneySupportingSections(props: {
  data: OverviewResponse;
  websiteId: string;
  flow: PriorityContentFlow | null;
  journey: CandidateJourney | null;
  returnQuery: string;
}) {
  return (
    <div class="journey-support-grid">
      <PriorityConcerns
        websiteId={props.websiteId}
        flow={props.flow}
        returnQuery={props.returnQuery}
      />
      <JourneyStructuralEvidence
        data={props.data}
        flow={props.flow}
        journey={props.journey}
      />
    </div>
  );
}

function PriorityConcerns(props: {
  websiteId: string;
  flow: PriorityContentFlow | null;
  returnQuery: string;
}) {
  return (
    <section class="signal-panel journey-opportunities">
      <div class="signal-panel-head">
        <div>
          <span class="signal-mini-kicker">Investigation queue</span>
          <h2>Priority concerns</h2>
          <p>
            Supported page and connection concerns affecting the selected
            current path.
          </p>
        </div>
      </div>
      <Show
        when={props.flow}
        keyed
        fallback={
          <p class="signal-muted">Select a current path to review concerns.</p>
        }
      >
        {(flow) => (
          <Show
            when={flow.concerns.length > 0}
            fallback={
              <p class="signal-muted">
                No priority structural concern was identified for this current
                path.
              </p>
            }
          >
            <ol>
              <ConcernRows
                rows={flow.concerns.slice(0, 3)}
                websiteId={props.websiteId}
                returnQuery={props.returnQuery}
                start={0}
              />
            </ol>
            <Show when={flow.concerns.length > 3}>
              <details class="journey-more-concerns">
                <summary>View all {flow.concerns.length} concerns</summary>
                <ol>
                  <ConcernRows
                    rows={flow.concerns.slice(3)}
                    websiteId={props.websiteId}
                    returnQuery={props.returnQuery}
                    start={3}
                  />
                </ol>
              </details>
            </Show>
          </Show>
        )}
      </Show>
    </section>
  );
}

function ConcernRows(props: {
  rows: PriorityFlowConcern[];
  websiteId: string;
  returnQuery: string;
  start: number;
}) {
  return (
    <For each={props.rows}>
      {(row, index) => (
        <li>
          <span class="signal-rank">{props.start + index() + 1}</span>
          <div>
            <span
              class={`journey-opportunity-severity is-${row.friction.issue?.severity ?? "review"}`}
            >
              {concernPriorityLabel(row)}
            </span>
            <strong>{row.friction.label}</strong>
            <p>{row.friction.detail}</p>
            <small>
              {row.affectedPages.length} affected{" "}
              {row.affectedPages.length === 1 ? "page" : "pages"} / Next step /{" "}
              {row.nextStep}
            </small>
          </div>
          <ConcernLink
            row={row}
            websiteId={props.websiteId}
            returnQuery={props.returnQuery}
          />
        </li>
      )}
    </For>
  );
}

function ConcernLink(props: {
  row: PriorityFlowConcern;
  websiteId: string;
  returnQuery: string;
}) {
  const primaryIssue = () => props.row.friction.issue;
  return (
    <div class="journey-concern-links">
      <Show
        when={primaryIssue()}
        keyed
        fallback={
          <Show when={props.row.friction.page} keyed>
            {(page) => (
              <a
                href={pageDetailHref(props.websiteId, page.id, {
                  source: "pages",
                  returnQuery: props.returnQuery,
                })}
              >
                Open page
              </a>
            )}
          </Show>
        }
      >
        {(issue) => (
          <a href={`/w/${props.websiteId}/scan/issues?issue=${issue.id}`}>
            Open issue
          </a>
        )}
      </Show>
      <Show when={props.row.issueRecords.length > 1}>
        <details>
          <summary>{props.row.issueRecords.length} issue records</summary>
          <For each={props.row.issueRecords}>
            {(issue, index) => (
              <a href={`/w/${props.websiteId}/scan/issues?issue=${issue.id}`}>
                Record {index() + 1}
              </a>
            )}
          </For>
        </details>
      </Show>
    </div>
  );
}

function concernPriorityLabel(row: PriorityFlowConcern): string {
  if (row.friction.kind === JOURNEY_FRICTION_KIND.BROKEN_LINK)
    return "Broken link";
  if (row.friction.kind === JOURNEY_FRICTION_KIND.NO_ONWARD_LINK) return "High";
  return row.friction.issue?.severity ?? "Review";
}
